import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Calendar, Clock, DollarSign } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { toast } from "sonner";

export default function CreateInvoice() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [hourlyRate, setHourlyRate] = useState(2500);
  const [selectedTimeEntries, setSelectedTimeEntries] = useState([]);
  const [customAmount, setCustomAmount] = useState("");
  const [notes, setNotes] = useState("");

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => base44.entities.Project.list(),
    enabled: !!selectedCustomer,
  });

  const { data: timeEntries = [] } = useQuery({
    queryKey: ['timeEntries', selectedCustomer, selectedProject, periodStart, periodEnd],
    queryFn: async () => {
      const filters = {
        related_to_type: 'customer',
        related_to_id: selectedCustomer,
        billable: true
      };
      
      const entries = await base44.entities.TimeEntry.filter(filters);
      
      return entries.filter(entry => {
        if (selectedProject && entry.project_id !== selectedProject) return false;
        if (periodStart && new Date(entry.start_time) < new Date(periodStart)) return false;
        if (periodEnd && new Date(entry.start_time) > new Date(periodEnd)) return false;
        return true;
      });
    },
    enabled: !!selectedCustomer,
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async (invoiceData) => {
      return base44.entities.Invoice.create(invoiceData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices']);
      toast.success('Faktura opprettet');
      navigate(createPageUrl('Invoices'));
    },
    onError: (error) => {
      toast.error('Kunne ikke opprette faktura: ' + error.message);
    }
  });

  const toggleTimeEntry = (entryId) => {
    setSelectedTimeEntries(prev =>
      prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]
    );
  };

  const selectedEntries = timeEntries.filter(e => selectedTimeEntries.includes(e.id));
  const totalHours = selectedEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
  const calculatedAmount = totalHours * hourlyRate;
  const finalAmount = customAmount ? parseFloat(customAmount) : calculatedAmount;
  const effectiveRate = totalHours > 0 ? finalAmount / totalHours : 0;

  const customer = customers.find(c => c.id === selectedCustomer);
  const project = projects.find(p => p.id === selectedProject);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      toast.error('Velg en kunde');
      return;
    }

    if (selectedTimeEntries.length === 0 && !customAmount) {
      toast.error('Velg tidsregistreringer eller angi et beløp');
      return;
    }

    const user = await base44.auth.me();

    const invoiceData = {
      customer_id: selectedCustomer,
      customer_name: customer?.company_name,
      project_id: selectedProject || null,
      project_name: project?.name || null,
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      period_start: periodStart || null,
      period_end: periodEnd || null,
      total_hours: totalHours,
      hourly_rate: hourlyRate,
      total_amount: finalAmount,
      effective_rate: effectiveRate,
      status: 'draft',
      time_entry_ids: selectedTimeEntries,
      line_items: selectedEntries.map(entry => ({
        description: entry.description || entry.task_title || 'Tidsregistrering',
        hours: (entry.duration_minutes || 0) / 60,
        rate: hourlyRate,
        amount: ((entry.duration_minutes || 0) / 60) * hourlyRate
      })),
      notes: notes,
      created_by: user.email,
      created_by_name: user.full_name
    };

    createInvoiceMutation.mutate(invoiceData);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link to={createPageUrl("Invoices")}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Opprett Faktura</h1>
            <p className="text-slate-600 mt-1">Velg kunde og tidsregistreringer for fakturagrunnlag</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Kundeinformasjon</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer">Kunde *</Label>
                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                  <SelectTrigger>
                    <SelectValue placeholder="Velg kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCustomer && (
                <div className="space-y-2">
                  <Label htmlFor="project">Prosjekt (valgfritt)</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger>
                      <SelectValue placeholder="Velg prosjekt eller la stå tom" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={null}>Alle prosjekter</SelectItem>
                      {projects.filter(p => p.related_to_id === selectedCustomer).map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Periode og Prising</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="periodStart">Periode Fra</Label>
                  <Input
                    id="periodStart"
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="periodEnd">Periode Til</Label>
                  <Input
                    id="periodEnd"
                    type="date"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hourlyRate">Timepris (NOK)</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseFloat(e.target.value))}
                />
              </div>
            </CardContent>
          </Card>

          {selectedCustomer && (
            <Card>
              <CardHeader>
                <CardTitle>Velg Tidsregistreringer</CardTitle>
              </CardHeader>
              <CardContent>
                {timeEntries.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    Ingen fakturerbare tidsregistreringer funnet for valgt periode
                  </p>
                ) : (
                  <div className="space-y-2">
                    {timeEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                      >
                        <Checkbox
                          checked={selectedTimeEntries.includes(entry.id)}
                          onCheckedChange={() => toggleTimeEntry(entry.id)}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">
                            {entry.task_title || entry.description || 'Tidsregistrering'}
                          </div>
                          <div className="text-sm text-slate-600 mt-1 space-x-3">
                            <span className="flex items-center gap-1 inline-flex">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(entry.start_time), 'dd MMM yyyy', { locale: nb })}
                            </span>
                            <span className="flex items-center gap-1 inline-flex">
                              <Clock className="w-3 h-3" />
                              {((entry.duration_minutes || 0) / 60).toFixed(1)}t
                            </span>
                            <span>{entry.user_name}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-slate-900">
                            {(((entry.duration_minutes || 0) / 60) * hourlyRate).toLocaleString('nb-NO')} kr
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Fakturadetaljer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Totalt antall timer:</span>
                  <span className="font-semibold">{totalHours.toFixed(2)} t</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Beregnet beløp:</span>
                  <span className="font-semibold">{calculatedAmount.toLocaleString('nb-NO')} kr</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <span className="text-slate-600">Effektiv timepris:</span>
                  <span className="font-semibold text-green-600">{effectiveRate.toLocaleString('nb-NO')} kr/t</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customAmount" className="text-base font-semibold">
                  Fakturabeløp (NOK) *
                </Label>
                <p className="text-xs text-slate-500">Beregnet fra timer: {calculatedAmount.toLocaleString('nb-NO')} kr. Juster ved behov.</p>
                <Input
                  id="customAmount"
                  type="number"
                  placeholder={calculatedAmount > 0 ? calculatedAmount.toString() : "Angi beløp"}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="text-lg font-semibold"
                />
                {customAmount && parseFloat(customAmount) !== calculatedAmount && (
                  <p className="text-xs text-amber-600">
                    ⚠ Manuelt justert fra beregnet {calculatedAmount.toLocaleString('nb-NO')} kr
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notater</Label>
                <Textarea
                  id="notes"
                  placeholder="Ekstra informasjon til fakturaen..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Link to={createPageUrl("Invoices")}>
              <Button type="button" variant="outline">
                Avbryt
              </Button>
            </Link>
            <Button type="submit" disabled={createInvoiceMutation.isPending}>
              {createInvoiceMutation.isPending ? 'Oppretter...' : 'Opprett Faktura'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}